import {
  DEFAULT_WORKBENCH_VARIANT,
  type ObjectRef,
  type SiteSubSurface,
  type WorkbenchActionSurfaceKey,
  type WorkbenchFocus,
  type WorkbenchVariant,
} from './workbench-focus';
import type { BusinessWorkspaceState } from './workbench-workspace-state';

export interface WorkbenchFocusAdapterOptions {
  actionProjection?: WorkbenchActionSurfaceKey;
  variant?: WorkbenchVariant;
  siteSubSurface?: SiteSubSurface;
  root?: ObjectRef;
  selection?: ObjectRef[];
  fallbackRoot?: ObjectRef;
}

function createFallbackRoot(state: BusinessWorkspaceState): ObjectRef {
  return {
    id: `${state.domain ?? 'domain'}:${state.subdomain ?? 'subdomain'}`,
    businessDomain: state.subdomain ? `${state.subdomain}_item` : 'entity',
  };
}

/** Resolves the active object ref from workspace selection/root (command palette SSOT). */
export function resolveActiveObjectRefFromWorkspaceState(
  state: BusinessWorkspaceState
): ObjectRef | undefined {
  const lastSelected =
    state.selection.length > 0 ? state.selection[state.selection.length - 1] : undefined;
  if (lastSelected?.id) {
    return lastSelected;
  }
  if (state.root?.id) {
    return state.root;
  }
  return undefined;
}

/**
 * WB3-P7.6 GAP-02: BusinessWorkspaceState를 SSOT로 두고 WorkbenchFocus를 파생한다.
 */
export function deriveWorkbenchFocusFromWorkspaceState(
  state: BusinessWorkspaceState,
  options: WorkbenchFocusAdapterOptions = {}
): WorkbenchFocus {
  const selection = [...(options.selection ?? state.selection)];
  const root =
    options.root ??
    state.root ??
    selection[selection.length - 1] ??
    options.fallbackRoot ??
    createFallbackRoot(state);
  const actionProjection = options.actionProjection ?? 'primary';

  return {
    root,
    selection,
    intent: state.intent,
    mode: state.mode,
    variant: options.variant ?? state.variant ?? DEFAULT_WORKBENCH_VARIANT,
    ...(options.siteSubSurface !== undefined ? { siteSubSurface: options.siteSubSurface } : {}),
    actionProjection,
    surface: actionProjection,
  };
}
